'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Coffee, Sun, Moon, UtensilsCrossed, Clock, Sparkles, ChevronDown, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MEAL_TYPE_ORDER, type DiningMenu, type PropertyDiningData } from '@/services/property-dining-types'

/**
 * Public dining showcase — the property's buffet menus presented as a reason
 * to book, deliberately WITHOUT prices. Grouped by meal, dish chips grouped by
 * course, service time from catering_meal_periods, CTA scrolling to #rooms.
 */

const MEAL_ICONS: Record<string, LucideIcon> = {
  breakfast: Coffee,
  lunch: Sun,
  dinner: Moon,
  suhoor: Moon,
  iftar: Moon,
  snack: UtensilsCrossed,
  any: UtensilsCrossed,
}

const COURSE_ORDER = ['appetizer', 'main', 'side', 'bread', 'dessert', 'beverage', 'other']

/** "06:30:00" (pg time) → "06:30". Manual slice — Intl 'ar' output differs
 *  between Node and WebKit and this text is in the SSR HTML. */
function formatServiceTime(time: string | null): string | null {
  if (!time) return null
  const match = time.match(/^(\d{2}):(\d{2})/)
  return match ? `${match[1]}:${match[2]}` : null
}

function localizedName(
  locale: string,
  en: string,
  ar: string | null | undefined
): string {
  return locale === 'ar' && ar ? ar : en
}

interface PropertyDiningProps {
  dining: PropertyDiningData
  locale: string
}

const DISH_PREVIEW_COUNT = 10

function DiningMenuCard({ menu, locale }: { menu: DiningMenu; locale: string }) {
  const t = useTranslations('booking.dining')
  const [expanded, setExpanded] = useState(false)

  // Order dishes by course so the card reads like a menu, not a stock list.
  const dishes = [...menu.dishes].sort(
    (a, b) => COURSE_ORDER.indexOf(a.course) - COURSE_ORDER.indexOf(b.course)
  )
  const visible = expanded ? dishes : dishes.slice(0, DISH_PREVIEW_COUNT)
  const hiddenCount = dishes.length - DISH_PREVIEW_COUNT

  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700/50 bg-white dark:bg-gray-800/50 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 leading-snug">
          {localizedName(locale, menu.name_en, menu.name_ar)}
        </h4>
        {menu.cuisine && (
          <span className="shrink-0 inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">
            {localizedName(locale, menu.cuisine.name_en, menu.cuisine.name_ar)}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {t('dishCount', { count: dishes.length })}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((dish) => (
          <span
            key={dish.id}
            className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300"
          >
            {localizedName(locale, dish.name_en, dish.name_ar)}
          </span>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 self-start inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {expanded ? t('showLess') : t('showMore', { count: hiddenCount })}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
        </button>
      )}
    </div>
  )
}

export function PropertyDining({ dining, locale }: PropertyDiningProps) {
  const t = useTranslations('booking.dining')

  if (dining.menus.length === 0) return null

  // Group menus by meal type, in serving order.
  const groups = MEAL_TYPE_ORDER.map((mealType) => ({
    mealType,
    menus: dining.menus.filter((m) => m.mealType === mealType),
    period: dining.mealPeriods.find((p) => p.mealType === mealType) ?? null,
  })).filter((g) => g.menus.length > 0)

  const scrollToRooms = () => {
    document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <section id="dining">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('title')}
        </h2>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
        {t('subtitle')}
      </p>

      <div className="space-y-8">
        {groups.map((group) => {
          const Icon = MEAL_ICONS[group.mealType] ?? UtensilsCrossed
          const serviceTime = formatServiceTime(group.period?.serviceTime ?? null)
          const mealLabel =
            (group.period &&
              localizedName(locale, group.period.label_en ?? '', group.period.label_ar)) ||
            t(`meals.${group.mealType}`)

          return (
            <div key={group.mealType}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">{mealLabel}</h3>
                {serviceTime && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700/50 px-2.5 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                    <Clock className="h-3 w-3" />
                    {t('servedAt', { time: serviceTime })}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.menus.map((menu) => (
                  <DiningMenuCard key={menu.id} menu={menu} locale={locale} />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Sell the room, not the meal — no prices on this section by design. */}
      <div className="mt-6 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 dark:from-primary/20 dark:via-primary/10 dark:to-accent/20 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700 dark:text-gray-300">{t('ctaText')}</p>
        </div>
        <button
          type="button"
          onClick={scrollToRooms}
          className="shrink-0 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t('ctaButton')}
        </button>
      </div>
    </section>
  )
}
